import { Query } from "@apollo/client/react/components";
import { until } from "@open-draft/until";
import FundamenteiWithText from "components/branding/FundamenteiWithText";
import LogoSizingProps from "components/branding/LogoSizingProps";
import IsolatedLayout from "components/layouts/IsolatedLayout";
import IsolatedScreener from "components/screener/v2/IsolatedScreener";
import ScreenerLandingPage from "components/screener/v2/ScreenerLandingPage";
import RunScreenerInputQuery from "components/screener/v2/query/RunScreenerInputQuery";
import ScreenerDefaultsCombinedQuery from "components/screener/v2/query/ScreenerDefaultsCombinedQuery";
import type {
  IRunScreenerInputQuery,
  IRunScreenerInputQueryVariables,
} from "components/screener/v2/query/__generated__/RunScreenerInputQuery.generated";
import type {
  IScreenerDefaultsCombinedQuery,
  IScreenerDefaultsCombinedQueryVariables,
} from "components/screener/v2/query/__generated__/ScreenerDefaultsCombinedQuery.generated";
import { DisplayCurrency } from "components/screener/v2/tags/DisplayCurrency";
import FreeModeBanner from "components/screener/v2/tags/FreeModeBanner";
import { Latency } from "components/screener/v2/tags/Latency";
import { RateLimit } from "components/screener/v2/tags/RateLimit";
import { StatementType } from "components/screener/v2/tags/StatementType";
import { TotalOfRows } from "components/screener/v2/tags/TotalOfRows";
import { loadSearchParams, useUrlSearchParams } from "components/screener/v2/urlSearchParams";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import { getYear } from "date-fns";
import useSelfie from "lib/hooks/useSelfie";
import isBrowser from "lib/utils/isBrowser";
import { fromInitialState } from "lib/withApollo";
import type { GetServerSidePropsContext, NextPage } from "next";
import Link from "next/link";
import { parseCookies } from "nookies";
import { useIntl } from "react-intl";
import { Value } from "react-powerplug";
import { useEffectOnce } from "react-use";
import { IFinancialsStatementType, IPlatformVertical } from "types/graphqlTypes";

interface PageProps {
  defaultRunScreenerInput: IScreenerDefaultsCombinedQuery["runScreenerInput"];
  screenerDefaults: IScreenerDefaultsCombinedQuery["screenerDefaults"];
}

const Page: NextPage<PageProps> = function Page({ screenerDefaults, defaultRunScreenerInput }) {
  const selfie = useSelfie();
  const intl = useIntl();
  const { state, setURLState } = useUrlSearchParams({
    defaultRunScreenerInput,
  });

  useEffectOnce(() => {
    try {
      if (localStorage) {
        localStorage.setItem("didSeeScreener", new Date().toISOString());
      }
      // eslint-disable-next-line no-empty
    } catch {}
  });

  if (!selfie) {
    return <ScreenerLandingPage />;
  }

  const isStocksOrReits =
    state.platformVertical === IPlatformVertical.Stocks || state.platformVertical === IPlatformVertical.Reits;
  const language = (["en-US", "pt-BR"].includes(intl.locale) ? intl.locale : "pt-BR") as "en-US" | "pt-BR";
  const inputObjectId = state.storedRunInputId;

  return (
    <Query<IRunScreenerInputQuery, IRunScreenerInputQueryVariables>
      query={RunScreenerInputQuery}
      variables={{
        objectId: inputObjectId || "",
        language,
      }}
      skip={inputObjectId === null}
      fetchPolicy={isBrowser() ? "cache-first" : "network-only"}
    >
      {({ data, previousData }) => {
        const runScreenerInput =
          inputObjectId === null ? null : ((data?.runScreenerInput || previousData?.runScreenerInput) ?? null);
        return (
          <IsolatedLayout>
            <FundamenteiMetadata
              title="Screener"
              description="Descubra os melhores ativos através dos mais de 900 indicadores fundamentalistas disponíveis"
            />
            <main className="relative flex h-svh flex-col overflow-y-hidden px-4 py-6 lg:overflow-auto lg:px-6 lg:py-8">
              <div className="flex h-full w-full flex-col gap-4">
                {!selfie.isPremium && (
                  <Value initial={false}>
                    {({ value: isClosed, set }) => (isClosed ? null : <FreeModeBanner onClose={() => set(true)} />)}
                  </Value>
                )}
                <IsolatedScreener
                  state={state}
                  setURLState={setURLState}
                  language={language}
                  runScreenerInput={runScreenerInput}
                  screenerDefaults={screenerDefaults}
                />
              </div>
              <footer className="mt-auto grid grid-cols-1 gap-y-4 pt-6 lg:grid-cols-3 landscape:max-lg:hidden">
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 lg:justify-normal lg:gap-2">
                  <StatementType statementType={IFinancialsStatementType.Annual} />
                  {isStocksOrReits ? (
                    <DisplayCurrency currency="USD" emoji="🇺🇸" />
                  ) : (
                    <DisplayCurrency currency="BRL" emoji="🇧🇷" />
                  )}
                  <TotalOfRows />
                  {!selfie.isPremium && <RateLimit />}
                  <Latency className="flex lg:hidden" />
                </div>

                <Link href="/" className="hidden scale-75 justify-center lg:flex lg:scale-100">
                  <FundamenteiWithText
                    {...LogoSizingProps.FundamenteiWithText.VERY_VERY_SMALL}
                    fill={null}
                    className="justify-center fill-brand dark:fill-white"
                  />
                </Link>

                <div className="hidden flex-wrap items-center justify-end gap-x-4 lg:flex">
                  <Latency className="hidden lg:flex" />
                </div>

                <Link
                  href="/"
                  className={classNames(
                    "flex justify-center text-xs font-bold tracking-wide text-slate-700",
                    "dark:text-white",
                    "pointer-events-none lg:pointer-events-auto lg:hidden",
                  )}
                >
                  ®Fundamentei {getYear(new Date())}
                </Link>
              </footer>
            </main>
          </IsolatedLayout>
        );
      }}
    </Query>
  );
};

export async function getServerSideProps({ query, locale, ...ctx }: GetServerSidePropsContext) {
  const cookies = parseCookies(ctx);
  if (!cookies.token) {
    return {
      props: {},
    };
  }

  const searchParams = loadSearchParams(query);
  const { data } = await until(async () => {
    const apolloClient = fromInitialState({}, []);
    const { data } = await apolloClient.query<IScreenerDefaultsCombinedQuery, IScreenerDefaultsCombinedQueryVariables>({
      query: ScreenerDefaultsCombinedQuery,
      variables: {
        objectId: searchParams.input || "",
        language: locale || "pt-BR",
      },
    });
    return data;
  });

  return {
    props: {
      defaultRunScreenerInput: data?.runScreenerInput || null,
      screenerDefaults: data?.screenerDefaults || null,
    },
  };
}

export default Page;
